import React from 'react';
import PropTypes from 'prop-types';
import createFragment from 'react-addons-create-fragment';
import classNames from 'classnames';
import SocialIcons from '../social_icons/index.jsx';
import LazyImg from '../lazy_img/index.jsx';
import { RA_73_STERILIZE_HTML_STRING } from '../utils';

const createDescriptionFragment = (label, value, classModifier) => {
    return createFragment({
        label: <dt className="c-team-members__description-label">{label}</dt>,
        value: <dd className={`c-team-members__description-value c-team-members__${classModifier}`}>{value}</dd>
    });
};

const TeamMember = props => {
    const {
        bio,
        designationString,
        email,
        fax,
        licenses,
        location,
        memberId,
        name,
        phone,
        phoneFormatted,
        photo,
        social,
        title,
        url,
        isHorizontalBioForced,
        teamMemberLabels
    } = props;

    const teamMemberClasses = classNames(
        'c-team-members__member col-xs-12',
        {
            'col-lg-6': !isHorizontalBioForced
        },
        {
            [`c-team-member-${memberId}`]: typeof memberId === 'number'
        }
    );

    const photoClasses = classNames('col-sm-5 col-xs-12', { 'col-lg-4': isHorizontalBioForced });
    const memberDataClasses = classNames(
        'col-xs-12',
        { 'col-sm-7': photo },
        { 'col-lg-8': photo && isHorizontalBioForced }
    );

    return (
        <div className={teamMemberClasses}>
            { photo &&
                <div className={photoClasses}>
                    { url ?
                        <a
                            href={url}
                            data-click-type="contact"
                            data-contact-method="website"
                            data-contact-value={url}
                        >
                            <LazyImg className="c-team-members__image" src={photo} alt={name || ""}/>
                        </a>
                        :
                        <div className="c-team-members__link-disabled">
                            <LazyImg className="c-team-members__image" src={photo} alt={name || ""} />
                        </div>
                    }
                </div>
            }
            <div className={memberDataClasses}>
                { name && <h3 className="c-team-members__name">{name}</h3> }
                { designationString &&
                    <div
                        className="c-team-members__p c-team-members__p--designations"
                        dangerouslySetInnerHTML={{__html: RA_73_STERILIZE_HTML_STRING(designationString) }}
                    />
                }
                { social &&
                    <SocialIcons
                        networks={social}
                        screenReaderText={`for ${name}`}
                    />
                }
                { bio &&
                    <div className="c-team-members__p c-team-members__bio"
                        dangerouslySetInnerHTML={{
                            __html: RA_73_STERILIZE_HTML_STRING(bio)
                        }}
                    />
                }
                <dl className="c-team-members__description-list">
                    { title && createDescriptionFragment(teamMemberLabels['job_title'], title, 'title') }
                    { licenses && createDescriptionFragment(teamMemberLabels['state_licenses'], licenses, 'licenses') }
                    { location && createDescriptionFragment(teamMemberLabels['location'], location, 'location') }
                    { url && createDescriptionFragment(teamMemberLabels['website'],
                        <a
                            href={url}
                            aria-label="Website"
                            data-click-type="contact"
                            data-contact-method="website"
                            data-contact-value={url}
                        >
                            {url}
                        </a>,
                        'website'
                    )}
                    { email && createDescriptionFragment(teamMemberLabels['email'],
                        <a
                            href={`mailto:${email}`}
                            data-click-type="contact"
                            data-contact-method="email"
                            data-contact-value={email}
                        >
                            {email}
                        </a>,
                        'email'
                    )}
                    { phone && createDescriptionFragment(teamMemberLabels['phone'],
                        <a
                            href={`tel:${phoneFormatted}`}
                            data-click-type="contact"
                            data-contact-method="phone"
                            data-contact-value={phone}
                        >
                            {phone}
                        </a>,
                        'phone'
                    )}
                    { fax && createDescriptionFragment(teamMemberLabels['fax'], fax, 'fax') }
                </dl>
            </div>
            <div
                className="col-sm-7 col-xs-12 hidden-md hidden-lg pull-right c-team-members__buttons"
            >
                { email &&
                    <a
                        className="btn btn-hs btn-block text-center pull-left c-team-members__button
                            c-team-members__email-button"
                        href={`mailto:${email}`}
                        data-click-type="contact"
                        data-contact-method="email"
                        data-contact-value={email}
                    >
                        {teamMemberLabels.emailMeLabel}
                    </a>
                }
                { phoneFormatted &&
                    <a
                        className="btn btn-hs btn-block text-center pull-left c-team-members__button
                            c-team-members__call-button"
                        href={`tel:${phoneFormatted}`}
                        data-click-type="contact"
                        data-contact-method="phone"
                        data-contact-value={phoneFormatted}
                    >
                        {teamMemberLabels.callMeLabel}
                    </a>
                }
            </div>
        </div>
    );
};

TeamMember.propTypes = {
    bio: PropTypes.string,
    designations: PropTypes.arrayOf(PropTypes.string),
    email: PropTypes.string,
    emptyTeamMembersText: PropTypes.string,
    fax: PropTypes.string,
    licenses: PropTypes.string,
    location: PropTypes.string,
    memberId: PropTypes.number,
    name: PropTypes.string,
    phone: PropTypes.string,
    phoneFormatted: PropTypes.string,
    photo: PropTypes.string,
    social: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            url: PropTypes.string,
            icon: PropTypes.string
        })
    ),
    title: PropTypes.string,
    url: PropTypes.string,
    teamMemberLabels: PropTypes.shape({
        emailMeLabel: PropTypes.string,
        callMeLabel: PropTypes.string,
        job_title: PropTypes.string,
        state_licenses: PropTypes.string,
        location: PropTypes.string,
        website: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        fax: PropTypes.string
    })
};

export default TeamMember;



// WEBPACK FOOTER //
// ./components/team_members/team-member.jsx